/**
 * Cloud configuration comes from public build-time variables only:
 *
 *   VITE_SUPABASE_URL              https://<project>.supabase.co
 *   VITE_SUPABASE_PUBLISHABLE_KEY  the publishable (or legacy anon) key
 *
 * Both are designed to be shipped to browsers; Row Level Security is what
 * protects the data. A privileged key (`sb_secret_…`, or a legacy JWT whose role
 * is `service_role`) must never reach a browser bundle, so one is refused here
 * even if it was put in the environment by mistake.
 */

export interface CloudConfig {
  url: string;
  key: string;
}

export type ConfigResult = { status: 'absent' } | { status: 'invalid'; reason: string } | { status: 'ok'; config: CloudConfig };

function jwtRole(token: string): string | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '='));
    const role = (JSON.parse(json) as { role?: unknown }).role;
    return typeof role === 'string' ? role : null;
  } catch {
    return null;
  }
}

/** True for any key that grants more than the browser's own rights. */
export function isPrivilegedKey(key: string): boolean {
  if (key.startsWith('sb_secret_')) return true;
  const role = jwtRole(key);
  return role !== null && role !== 'anon';
}

export function parseCloudConfig(env: { VITE_SUPABASE_URL?: string; VITE_SUPABASE_PUBLISHABLE_KEY?: string; VITE_SUPABASE_ANON_KEY?: string }): ConfigResult {
  const url = (env.VITE_SUPABASE_URL ?? '').trim().replace(/\/+$/, '');
  const key = (env.VITE_SUPABASE_PUBLISHABLE_KEY ?? env.VITE_SUPABASE_ANON_KEY ?? '').trim();
  if (!url && !key) return { status: 'absent' };
  if (!url || !key) return { status: 'invalid', reason: 'Both VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY are needed.' };
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return { status: 'invalid', reason: 'VITE_SUPABASE_URL is not a valid URL.' };
  }
  const local = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
  if (parsed.protocol !== 'https:' && !(local && parsed.protocol === 'http:')) return { status: 'invalid', reason: 'VITE_SUPABASE_URL must use https.' };
  if (isPrivilegedKey(key)) return { status: 'invalid', reason: 'A privileged Supabase key was supplied. Only the publishable (anon) key may be used in the browser; it was ignored.' };
  return { status: 'ok', config: { url: parsed.origin, key } };
}
